import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grape } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [newPassword, setNewPassword] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: newPassword });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail, t) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16 sm:py-24">
      <div className="text-center mb-8">
        <Grape className="h-8 w-8 text-[#4ba3b7] mx-auto mb-3" />
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">{t("auth.resetPasswordTitle")}</h1>
      </div>

      {success ? (
        <p className="text-center text-sm text-[#34C759] font-semibold" data-testid="reset-password-success-message">
          {t("auth.passwordResetSuccessMessage")}
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="newPassword">{t("auth.newPassword")}</Label>
            <Input id="newPassword" type="password" required minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                   className="h-12 rounded-xl mt-1.5" data-testid="reset-password-new-password-input" />
          </div>
          {error && <p className="text-sm text-[#FF3B30]" data-testid="reset-password-error-message">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-[#4ba3b7] hover:bg-[#3D8A9A] font-bold" data-testid="reset-password-submit-button">
            {loading ? t("common.loading") : t("auth.resetPasswordButton")}
          </Button>
        </form>
      )}

      <p className="text-center text-sm mt-6">
        <Link to="/login" className="text-[#4ba3b7] font-semibold hover:underline" data-testid="reset-password-back-to-login-link">
          {t("auth.backToLogin")}
        </Link>
      </p>
    </div>
  );
}
